import { useEffect, useRef, useState } from 'react'

import type {
  TrendBucket,
  TrendSeries,
} from './TrendChart'

/**
 * Picks an initial bucket from a date-range length. Client-side heuristic
 * focused on user-perceptible chart density: 30 dots is readable, 365 dots
 * is not. The server can still override this via `bucket` on the input.
 *
 * Note: this differs from the backend's `pickPeriodBucket` (which chooses
 * a *wider-than-range* zoom-out bucket for heatmap context). This frontend
 * heuristic picks an *appropriate-for-range* bucket for line chart density.
 */
export const pickClientBucket = (rangeMs: number): TrendBucket => {
  const days = rangeMs / (1000 * 60 * 60 * 24)
  if (days <= 60) return 'DAY'
  if (days <= 180) return 'WEEK'
  return 'MONTH'
}

/**
 * Tracks the active bucket for a trend chart. Initializes from the date
 * range via `pickClientBucket`, and re-applies the heuristic any time the
 * date range changes (e.g. user switches from a 30-day to a 1-year window
 * — we don't want to keep DAY bucketing and render 365 dots).
 *
 * Takes the range as numeric timestamps (`startMs`/`endMs`) rather than
 * `Date` objects so the effect compares values, not references. Callers
 * passing fresh `new Date(...)` each render would otherwise re-trigger
 * the effect on every parent re-render.
 *
 * Returns the standard `[value, setter]` shape so callers can pass the
 * setter straight into a bucket toggle.
 */
export const useAutoBucket = (
  startMs: number,
  endMs: number
): [TrendBucket, (b: TrendBucket) => void] => {
  const [bucket, setBucket] = useState<TrendBucket>(() =>
    pickClientBucket(endMs - startMs)
  )

  // Skip the very first fire — useState above already initialized from the
  // same range. Subsequent range changes do trigger re-pick.
  const isFirstRange = useRef(true)
  useEffect(() => {
    if (isFirstRange.current) {
      isFirstRange.current = false
      return
    }
    setBucket(pickClientBucket(endMs - startMs))
  }, [startMs, endMs])

  return [bucket, setBucket]
}

/**
 * Calls `refetch()` whenever `refreshKey` changes — except on the initial
 * mount, since the useQuery that owns `refetch` already kicked off the
 * first fetch. Lets a page-level Refresh button trigger a manual reload
 * of every container without each container needing its own subscribe
 * machinery.
 */
export const useRefetchOnKeyChange = (
  refetch: () => void,
  refreshKey: number
): void => {
  const isFirstFire = useRef(true)
  useEffect(() => {
    if (isFirstFire.current) {
      isFirstFire.current = false
      return
    }
    refetch()
  }, [refreshKey, refetch])
}

/**
 * Maps server-side series (indexed by some categorical key) into the
 * frontend's `TrendSeries[]` shape, walking the palette to fix display
 * order + label + color. Missing server keys become empty-points series
 * so the chart still renders a flat line for that category.
 *
 * Parameters:
 *   palette    — display order + label + color for each categorical key
 *   serverSeries — raw server response (in any order)
 *   getServerKey — extracts the categorical key from a server-series row
 */
export const buildTrendSeriesFromPalette = <
  ServerSeriesShape extends {
    points: ReadonlyArray<{ periodStart: Date | string; count: number }>
  },
>(
  palette: ReadonlyArray<{ id: string; label: string; color: string }>,
  serverSeries: ReadonlyArray<ServerSeriesShape>,
  getServerKey: (s: ServerSeriesShape) => string
): TrendSeries[] => {
  const byKey = new Map<string, ServerSeriesShape>()
  for (const s of serverSeries) byKey.set(getServerKey(s), s)
  return palette.map((p) => {
    const found = byKey.get(p.id)
    return {
      id: p.id,
      label: p.label,
      color: p.color,
      points: (found?.points ?? []).map((pt) => ({
        periodStart: pt.periodStart,
        count: pt.count,
      })),
    }
  })
}
